import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Check, 
  X, 
  Zap, 
  Crown, 
  Shield,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Menu
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useConfig } from '../contexts/ConfigContext';
import UpgradeModal from '../components/UpgradeModal';
import SEO, { SEO_PRESETS } from '../components/SEO';
import { breakpoints } from '../../styles/theme.js';

const PricingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { tier } = useSubscription();
  const { config, getPricing } = useConfig();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const mobile = windowWidth < parseInt(breakpoints.mobile);

  // Get dynamic pricing
  const proMonthly = getPricing('pro', 'monthly');
  const proAnnual = getPricing('pro', 'annual');
  
  // Calculate values with fallbacks
  const monthlyPrice = proMonthly?.amount || 7.99;
  const annualPrice = proAnnual?.amount || 49.99;
  const annualMonthlyPrice = (annualPrice / 12).toFixed(2);

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      minHeight: '100vh',
      fontFamily: '"DM Sans", system-ui, sans-serif',
    },
    header: {
      padding: '1.5rem 2rem 0.75rem',
      textAlign: 'center',
      maxWidth: '800px',
      margin: '0 auto',
    },
    headline: {
      fontSize: 'clamp(2rem, 4vw, 2.75rem)',
      fontWeight: '900',
      lineHeight: '1.1',
      marginBottom: '0.75rem',
      background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #fbbf24 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    subheadline: {
      fontSize: '1.05rem',
      color: '#94a3b8',
      marginBottom: '1.5rem',
      lineHeight: '1.5',
    },
    // Billing Toggle
    toggleContainer: {
      display: 'inline-flex',
      background: 'rgba(30, 41, 59, 0.6)',
      border: '1px solid rgba(100, 116, 139, 0.3)',
      borderRadius: '12px',
      padding: '0.5rem',
      marginBottom: '3rem',
    },
    toggleButton: {
      padding: '0.75rem 2rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s',
    },
    // Price Anchoring - Show from high to low
    pricingGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
      gap: '1.25rem',
      maxWidth: '900px',
      margin: '0 auto',
      padding: '1rem 2rem 2rem',
    },
    priceCard: {
      background: 'rgba(15, 23, 42, 0.8)',
      border: '2px solid rgba(100, 116, 139, 0.3)',
      borderRadius: '16px',
      padding: '1.5rem 1.25rem',
      position: 'relative',
      transition: 'all 0.3s',
    },
    priceCardHighlighted: {
      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
      border: '2px solid #fbbf24',
      boxShadow: '0 20px 60px rgba(251, 191, 36, 0.2)',
      transform: 'scale(1.05)',
    },
    // Feature Comparison Table
    comparisonTable: {
      maxWidth: '1200px',
      margin: '5rem auto',
      padding: '2rem',
    },
    tableRow: {
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr 1fr',
      gap: '1rem',
      padding: '1.5rem',
      borderBottom: '1px solid rgba(100, 116, 139, 0.2)',
      alignItems: 'center',
    },
    // FAQ
    faqSection: {
      maxWidth: '800px',
      margin: '5rem auto',
      padding: '2rem',
    },
    faqItem: {
      background: 'rgba(30, 41, 59, 0.6)',
      border: '1px solid rgba(100, 116, 139, 0.3)',
      borderRadius: '12px',
      padding: '1.5rem',
      marginBottom: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
    },
  };

  const features = [
    {
      name: 'Daily Rounds',
      anonymous: '3',
      free: '8',
      pro: 'Unlimited',
    },
    {
      name: 'Training Dimensions',
      anonymous: '2 (Position, Audio)',
      free: '3 (Position, Color, Audio)',
      pro: 'All 24 Dimensions',
    },
    {
      name: 'Dimension Combinations',
      anonymous: 'Max 2',
      free: 'Max 2',
      pro: 'Unlimited',
    },
    {
      name: 'Progress History',
      anonymous: 'Not saved',
      free: '30 days',
      pro: 'Unlimited',
    },
    {
      name: 'Favorite Combos',
      anonymous: '0',
      free: '3',
      pro: 'Unlimited',
    },
    {
      name: 'Advanced Analytics',
      anonymous: false,
      free: false,
      pro: true,
    },
    {
      name: 'Premium Content',
      anonymous: false,
      free: false,
      pro: true,
    },
    {
      name: 'Training Protocols',
      anonymous: false,
      free: false,
      pro: true,
    },
  ];

  const faqs = [
    {
      question: "Can I switch between monthly and annual billing?",
      answer: "Yes! You can upgrade from monthly to annual anytime to lock in the savings. You can also downgrade at the end of your billing cycle.",
    },
    {
      question: "What happens if I downgrade from Pro to Free?",
      answer: "You'll keep access to Pro features until the end of your billing period. After that, you'll return to the free tier with 12 rounds/day and 3 dimensions. Your history beyond 30 days will be preserved and restored if you upgrade again.",
    },
    {
      question: "Do you offer refunds?",
      answer: "Yes! We offer a 30-day money-back guarantee on all Pro subscriptions. If you're not satisfied, contact us for a full refund, no questions asked.",
    },
    {
      question: "Can I try Pro before buying?",
      answer: "While we don't offer a Pro trial, you can use the free tier (12 rounds/day, 3 dimensions) to experience the training. Most users find this sufficient to evaluate whether Pro is worth it for them.",
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, Mastercard, American Express, Discover) through our secure payment processor, Stripe.",
    },
  ];

  const handleCTAClick = (plan) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }

    if (plan === 'monthly' || plan === 'annual') {
      setShowUpgradeModal(true);
    }
  };

  return (
    <div style={styles.container}>
      <SEO {...SEO_PRESETS.pricing} />
      
      {/* Navigation */}
      <nav style={{
        padding: mobile ? '1rem 1rem' : '1.5rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        maxWidth: '1200px',
        margin: '0 auto',
        gap: mobile ? '0.75rem' : '0',
      }}>
        <div 
          onClick={() => navigate('/')}
          style={{
            fontSize: mobile ? '1.25rem' : '1.5rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            cursor: 'pointer',
          }}
        >
          IQ Reps
        </div>
        <div style={{ 
          display: 'flex', 
          gap: mobile ? '0.5rem' : '1rem', 
          alignItems: 'center',
          flex: 1,
          justifyContent: 'flex-end'
        }}>
          {!mobile && (
            <>
              <button
                onClick={() => navigate('/articles')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                }}
              >
                Articles
              </button>
              <button
                onClick={() => navigate('/pricing')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#60a5fa',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  fontWeight: '600'
                }}
              >
                Pricing
              </button>
            </>
          )}
          {mobile && (
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                style={{
                  background: 'transparent',
                  border: '1px solid #475569',
                  color: '#94a3b8',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}
              >
                <Menu size={18} />
                <ChevronDown size={14} />
              </button>
              {mobileMenuOpen && (
                <div 
                  style={{
                    position: 'absolute',
                    right: '0',
                    top: 'calc(100% + 0.5rem)',
                    background: '#1e293b',
                    border: '1px solid #475569',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    minWidth: '140px',
                    zIndex: 1000,
                    boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
                  }}>
                  <button
                    onClick={() => {
                      navigate('/articles');
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#e2e8f0',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      textAlign: 'left',
                      borderBottom: '1px solid #334155'
                    }}
                  >
                    Articles
                  </button>
                  <button
                    onClick={() => {
                      navigate('/pricing');
                      setMobileMenuOpen(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#60a5fa',
                      fontSize: '0.9rem',
                      cursor: 'pointer',
                      padding: '0.75rem 1rem',
                      width: '100%',
                      textAlign: 'left',
                      fontWeight: '600'
                    }}
                  >
                    Pricing
                  </button>
                </div>
              )}
            </div>
          )}
          {!isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/login')}
                style={{
                  background: 'transparent',
                  border: '1px solid #475569',
                  color: '#e2e8f0',
                  fontSize: mobile ? '0.9rem' : '1rem',
                  fontWeight: '600',
                  padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/signup')}
                style={{
                  background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                  border: 'none',
                  color: 'white',
                  fontSize: mobile ? '0.9rem' : '1rem',
                  fontWeight: '600',
                  padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Sign Up Free
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/n-back')}
              style={{
                background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
                border: 'none',
                color: 'white',
                fontSize: mobile ? '0.9rem' : '1rem',
                fontWeight: '600',
                padding: mobile ? '0.5rem 1rem' : '0.5rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Go to {isAuthenticated ? 'Dashboard' : 'Training'}
            </button>
          )}
        </div>
      </nav>

      {/* Header */}
      <header style={{...styles.header, padding: '2rem 2rem 1rem'}}>
        <h1 style={styles.headline}>
          Simple, Transparent Pricing
        </h1>
        <p style={{...styles.subheadline, marginBottom: '0'}}>
          Start free with 12 rounds daily, upgrade anytime for unlimited access to all 24 dimensions
        </p>
      </header>

      {/* Pricing Cards - All Three Tiers */}
      <section style={styles.pricingGrid}>
        {/* Free Tier */}
        <div style={styles.priceCard}>
          <Zap size={36} color="#a78bfa" style={{ marginBottom: '0.5rem' }} />
          
          <h2 style={{
            fontSize: '1.35rem',
            fontWeight: '800',
            color: '#a78bfa',
            marginBottom: '0.5rem',
          }}>
            Free
          </h2>
          
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              fontSize: '2.25rem',
              fontWeight: '900',
              color: '#e2e8f0',
              lineHeight: '1',
            }}>
              $0
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: '#94a3b8',
              marginTop: '0.4rem',
            }}>
              forever free
            </div>
          </div>

          <button
            onClick={() => navigate('/signup')}
            disabled={isAuthenticated}
            style={{
              width: '100%',
              padding: '0.85rem',
              background: isAuthenticated ? 'rgba(100, 116, 139, 0.3)' : 'transparent',
              border: `2px solid ${isAuthenticated ? 'rgba(100, 116, 139, 0.5)' : '#a78bfa'}`,
              borderRadius: '10px',
              color: isAuthenticated ? '#64748b' : '#a78bfa',
              fontSize: '0.95rem',
              fontWeight: '700',
              cursor: isAuthenticated ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            {isAuthenticated ? (tier === 'free' ? 'Current Plan' : 'Signed In') : 'Sign Up Free'}
            {!isAuthenticated && <ArrowRight size={18} />}
          </button>

          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}>
            {[
              { text: '12 rounds per day', available: true },
              { text: '3 training dimensions', available: true },
              { text: 'Max 2 combinations', available: true },
              { text: '30-day progress history', available: true },
              { text: '3 favorite combos', available: true },
              { text: 'Save your progress', available: true },
              { text: 'Advanced analytics', available: false },
              { text: 'Premium protocols', available: false },
            ].map((feature, i) => (
              <li key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
                color: feature.available ? '#94a3b8' : '#64748b',
                fontSize: '0.9rem',
              }}>
                {feature.available ? (
                  <Check size={16} color="#10b981" />
                ) : (
                  <X size={16} color="#64748b" />
                )}
                <span>{feature.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pro Monthly */}
        <div style={styles.priceCard}>
          <Crown size={36} color="#60a5fa" style={{ marginBottom: '0.5rem' }} />
          
          <h2 style={{
            fontSize: '1.35rem',
            fontWeight: '800',
            color: '#60a5fa',
            marginBottom: '0.5rem',
          }}>
            Pro Monthly
          </h2>
          
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              fontSize: '2.25rem',
              fontWeight: '900',
              color: '#e2e8f0',
              lineHeight: '1',
            }}>
              ${monthlyPrice.toFixed(2)}
            </div>
            <div style={{
              fontSize: '0.85rem',
              color: '#94a3b8',
              marginTop: '0.4rem',
            }}>
              per month
            </div>
            <div style={{
              fontSize: '0.8rem',
              color: '#64748b',
              marginTop: '0.2rem',
            }}>
              Cancel anytime
            </div>
          </div>

          <button
            onClick={() => handleCTAClick('monthly')}
            disabled={tier === 'paid'}
            style={{
              width: '100%',
              padding: '0.85rem',
              background: tier === 'paid' 
                ? 'rgba(100, 116, 139, 0.3)'
                : 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
              border: 'none',
              borderRadius: '10px',
              color: tier === 'paid' ? '#64748b' : 'white',
              fontSize: '0.95rem',
              fontWeight: '700',
              cursor: tier === 'paid' ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            {tier === 'paid' ? 'Current Plan' : 'Get Started'}
            {tier !== 'paid' && <ArrowRight size={20} />}
          </button>

          <ul style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}>
            {[
              'Unlimited rounds per day',
              'All 24 training dimensions',
              'Unlimited combinations',
              'Unlimited progress history',
              'Advanced analytics',
              'Premium training protocols',
              'Priority support',
            ].map((feature, i) => (
              <li key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.5rem',
                color: '#cbd5e1',
                fontSize: '0.9rem',
              }}>
                <Check size={16} color="#10b981" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Pro Annual - Highest Value (Show First for Anchoring) */}
        <div style={{ ...styles.priceCard, ...styles.priceCardHighlighted }}>
          <div style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#fbbf24',
            color: '#1f2937',
            padding: '0.35rem 1.25rem',
            borderRadius: '16px',
            fontSize: '0.8rem',
            fontWeight: '700',
          }}>
            BEST VALUE
          </div>
            
            <Crown size={36} color="#fbbf24" style={{ marginBottom: '0.5rem', marginTop: '0.5rem' }} />
            
            <h2 style={{
              fontSize: '1.35rem',
              fontWeight: '800',
              color: '#fbbf24',
              marginBottom: '0.5rem',
            }}>
              Pro Annual
            </h2>
            
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                fontSize: '2.25rem',
                fontWeight: '900',
                color: '#e2e8f0',
                lineHeight: '1',
              }}>
                ${annualPrice.toFixed(2)}
              </div>
              <div style={{
                fontSize: '0.85rem',
                color: '#94a3b8',
                marginTop: '0.4rem',
              }}>
                per year (${annualMonthlyPrice}/month)
              </div>
              <div style={{
                fontSize: '0.8rem',
                color: '#10b981',
                marginTop: '0.2rem',
                fontWeight: '600',
              }}>
                Save ${(monthlyPrice * 12 - annualPrice).toFixed(0)} vs monthly
              </div>
            </div>

            <button
              onClick={() => handleCTAClick('annual')}
              style={{
                width: '100%',
                padding: '0.85rem',
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                border: 'none',
                borderRadius: '10px',
                color: '#1f2937',
                fontSize: '0.95rem',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                marginBottom: '1rem',
              }}
            >
              Get Started
              <ArrowRight size={18} />
            </button>

            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}>
              {[
                'Unlimited rounds per day',
                'All 24 training dimensions',
                'Unlimited combinations',
                'Unlimited progress history',
                'Advanced analytics',
                'Premium training protocols',
                'Priority support',
                'Lock in your rate',
              ].map((feature, i) => (
                <li key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  color: '#cbd5e1',
                  fontSize: '0.9rem',
                }}>
                  <Check size={16} color="#10b981" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
      </section>


      {/* Feature Comparison Table */}
      <section style={{...styles.comparisonTable, padding: mobile ? '1rem' : '2rem'}}>
        <h2 style={{
          fontSize: mobile ? '1.75rem' : 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '800',
          color: '#e2e8f0',
          marginBottom: mobile ? '2rem' : '3rem',
          textAlign: 'center',
        }}>
          Detailed Feature Comparison
        </h2>

        <div style={{
          background: 'rgba(30, 41, 59, 0.6)',
          border: '1px solid rgba(100, 116, 139, 0.3)',
          borderRadius: '16px',
          overflow: mobile ? 'auto' : 'hidden',
          maxWidth: '100%'
        }}>
          {/* Header Row */}
          <div style={{
            ...styles.tableRow,
            background: 'rgba(15, 23, 42, 0.8)',
            fontWeight: '700',
            color: '#e2e8f0',
            fontSize: mobile ? '0.85rem' : '1.1rem',
            gridTemplateColumns: mobile ? '1.5fr 0.8fr 0.8fr 0.8fr' : '2fr 1fr 1fr 1fr',
            minWidth: mobile ? '500px' : 'auto',
            padding: mobile ? '1rem 0.75rem' : '1.5rem',
            gap: mobile ? '0.5rem' : '1rem'
          }}>
            <div>Feature</div>
            <div style={{ textAlign: 'center' }}>Trial</div>
            <div style={{ textAlign: 'center' }}>Free</div>
            <div style={{ textAlign: 'center' }}>Pro</div>
          </div>

          {/* Feature Rows */}
          {features.map((feature, index) => (
            <div key={index} style={{
              ...styles.tableRow,
              gridTemplateColumns: mobile ? '1.5fr 0.8fr 0.8fr 0.8fr' : '2fr 1fr 1fr 1fr',
              minWidth: mobile ? '500px' : 'auto',
              padding: mobile ? '1rem 0.75rem' : '1.5rem',
              gap: mobile ? '0.5rem' : '1rem',
              fontSize: mobile ? '0.85rem' : '1rem'
            }}>
              <div style={{ color: '#cbd5e1', fontWeight: '600' }}>
                {feature.name}
              </div>
              <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                {typeof feature.anonymous === 'boolean' ? (
                  feature.anonymous ? (
                    <Check size={24} color="#10b981" />
                  ) : (
                    <X size={24} color="#64748b" />
                  )
                ) : (
                  feature.anonymous
                )}
              </div>
              <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                {typeof feature.free === 'boolean' ? (
                  feature.free ? (
                    <Check size={24} color="#10b981" />
                  ) : (
                    <X size={24} color="#64748b" />
                  )
                ) : (
                  feature.free
                )}
              </div>
              <div style={{ textAlign: 'center', color: '#fbbf24', fontWeight: '600' }}>
                {typeof feature.pro === 'boolean' ? (
                  feature.pro ? (
                    <Check size={24} color="#fbbf24" />
                  ) : (
                    <X size={24} color="#64748b" />
                  )
                ) : (
                  feature.pro
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section style={styles.faqSection}>
        <h2 style={{
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '800',
          color: '#e2e8f0',
          marginBottom: '1rem',
          textAlign: 'center',
        }}>
          Frequently Asked Questions
        </h2>
        <p style={{
          textAlign: 'center',
          color: '#94a3b8',
          marginBottom: '3rem',
          fontSize: '1.1rem',
        }}>
          Everything you need to know about pricing and features
        </p>

        {faqs.map((faq, index) => (
          <div
            key={index}
            style={styles.faqItem}
            onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#60a5fa';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.3)';
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: '700',
                color: '#e2e8f0',
                margin: 0,
              }}>
                {faq.question}
              </h3>
              {expandedFaq === index ? (
                <ChevronUp size={24} color="#60a5fa" />
              ) : (
                <ChevronDown size={24} color="#94a3b8" />
              )}
            </div>
            {expandedFaq === index && (
              <p style={{
                marginTop: '1rem',
                color: '#94a3b8',
                lineHeight: '1.6',
                fontSize: '1rem',
              }}>
                {faq.answer}
              </p>
            )}
          </div>
        ))}
      </section>

      {/* Trust Signals */}
      <section style={{
        maxWidth: '1000px',
        margin: '5rem auto',
        padding: '3rem 2rem',
        background: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '16px',
        textAlign: 'center',
      }}>
        <h3 style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          color: '#e2e8f0',
          marginBottom: '2rem',
        }}>
          Your Purchase is Protected
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem',
        }}>
          <div>
            <Shield size={48} color="#10b981" style={{ marginBottom: '1rem' }} />
            <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.5rem' }}>
              30-Day Money-Back Guarantee
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
              Not satisfied? Get a full refund, no questions asked
            </div>
          </div>
          <div>
            <Shield size={48} color="#60a5fa" style={{ marginBottom: '1rem' }} />
            <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.5rem' }}>
              Secure Payment Processing
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
              All transactions secured by Stripe
            </div>
          </div>
          <div>
            <Shield size={48} color="#a78bfa" style={{ marginBottom: '1rem' }} />
            <div style={{ fontSize: '1.1rem', fontWeight: '600', color: '#e2e8f0', marginBottom: '0.5rem' }}>
              Cancel Anytime
            </div>
            <div style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
              No contracts or commitments required
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section style={{
        padding: '5rem 2rem',
        textAlign: 'center',
        background: 'linear-gradient(135deg, rgba(96, 165, 250, 0.1) 0%, rgba(167, 139, 250, 0.1) 100%)',
      }}>
        <h2 style={{
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: '800',
          color: '#e2e8f0',
          marginBottom: '1rem',
        }}>
          Ready to Start Training?
        </h2>
        <p style={{
          fontSize: '1.2rem',
          color: '#94a3b8',
          marginBottom: '2rem',
          maxWidth: '600px',
          margin: '0 auto 2rem',
        }}>
          {isAuthenticated 
            ? "Choose the plan that's right for you" 
            : "Try 3 free rounds today, no credit card required"}
        </p>
        <button
          onClick={() => isAuthenticated ? setShowUpgradeModal(true) : navigate('/n-back')}
          style={{
            padding: '1.5rem 3rem',
            background: 'linear-gradient(135deg, #60a5fa 0%, #a78bfa 100%)',
            border: 'none',
            borderRadius: '12px',
            color: 'white',
            fontSize: '1.25rem',
            fontWeight: '700',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem',
            boxShadow: '0 10px 30px rgba(96, 165, 250, 0.3)',
          }}
        >
          {isAuthenticated ? 'Upgrade Now' : 'Start MY Free Training'}
          <ArrowRight size={24} />
        </button>
      </section>

      {/* Modals */}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
        />
      )}

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid rgba(100, 116, 139, 0.3)',
        padding: '2rem',
        textAlign: 'center',
        color: '#64748b',
        fontSize: '0.9rem',
      }}>
        <div style={{ marginBottom: '1rem' }}>
          <a href="/" style={{ color: '#94a3b8', textDecoration: 'none', margin: '0 1rem' }}>Home</a>
          <a href="/game" style={{ color: '#94a3b8', textDecoration: 'none', margin: '0 1rem' }}>Try Free</a>
        </div>
        © 2024 Multi N-Back. All rights reserved.
      </footer>
    </div>
  );
};

export default PricingPage;

